import axios from "axios";
import { getApiBaseUrl } from "src/config/endpoints";
import { getApiKey } from "./NotificationAnnouncementsAPIs";

const API_BASE_URL = getApiBaseUrl();

export interface PushSubscription {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

class NotificationService {
  private static urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  static async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if ("serviceWorker" in navigator && "PushManager" in window) {
      try {
        // `updateViaCache: "none"` forces the browser to fetch service-worker.js
        // from the network (not the HTTP cache) on every check, and update()
        // triggers that check now — together they ensure an updated worker (e.g.
        // one that renders the push `data.icon` logo) is picked up promptly
        // instead of users being stuck on a stale cached version.
        const registration = await navigator.serviceWorker.register(
          "/service-worker.js",
          { updateViaCache: "none" }
        );
        registration.update();
        return registration;
      } catch {
        return null;
      }
    }
    return null;
  }

  static async getVapidPublicKey(): Promise<string> {
    const apiKey = getApiKey();
    if (!apiKey) {throw new Error("No API key found");}

    const response = await axios.get(
      `${API_BASE_URL}/push-notification/vapid-public-key`,
      { headers: { Authorization: `Bearer ${apiKey}` } }
    );
    return response.data.publicKey;
  }

  // POST /api/push-notification/subscribe
  static async subscribeUserToPush(): Promise<PushSubscription | null> {
    try {
      const registration = await navigator.serviceWorker.ready;
      const vapidPublicKey = await this.getVapidPublicKey();
      const publicKey = this.urlBase64ToUint8Array(vapidPublicKey);

      let sub = await registration.pushManager.getSubscription();
      if (!sub) {
        sub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: publicKey as BufferSource,
        });
      }

      const apiKey = getApiKey();
      if (!apiKey) {throw new Error("No API key found");}

      const subscriptionData = sub.toJSON();

      const response = await axios.post(
        `${API_BASE_URL}/push-notification/subscribe`,
        {
          endpoint: subscriptionData.endpoint,
          keys: subscriptionData.keys,
        },
        { headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" } }
      );

      if (!response.data.status) {
        throw new Error("Push subscription failed on backend");
      }

      return subscriptionData as unknown as PushSubscription;
    } catch {
      return null;
    }
  }

  // DELETE /api/push-notification/unsubscribe?endpoint=<encoded>
  static async unsubscribeFromPush(): Promise<boolean> {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();

        const apiKey = getApiKey();
        if (!apiKey) {throw new Error("No API key found");}

        await axios.delete(
          `${API_BASE_URL}/push-notification/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`,
          {
            headers: {
              Authorization: `Bearer ${apiKey}`,
              "Content-Type": "application/json",
            },
          }
        );
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  static async checkPermission(): Promise<NotificationPermission> {
    if (!("Notification" in window)) {return "denied";}
    return Notification.permission;
  }

  static async requestPermission(): Promise<NotificationPermission> {
    if (!("Notification" in window)) {return "denied";}
    return Notification.requestPermission();
  }
}

export default NotificationService;
